import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const ToastContext = createContext(null);

let idCounter = 1;
function nextId() {
  idCounter += 1;
  return `t-${Date.now()}-${idCounter}`;
}

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const timeouts = useRef({});

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    clearTimeout(timeouts.current[id]);
    delete timeouts.current[id];
  }, []);

  const push = useCallback(
    ({ tone = "info", title, message, duration = 4500 }) => {
      const id = nextId();
      setItems((prev) => [...prev, { id, tone, title, message }]);
      if (duration > 0) {
        timeouts.current[id] = setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      push,
      dismiss,
      success: (message, title = "Done") => push({ tone: "success", title, message }),
      error: (message, title = "Something went wrong") =>
        push({ tone: "danger", title, message, duration: 6500 }),
      info: (message, title = "Heads up") => push({ tone: "info", title, message })
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed top-3 sm:top-4 left-1/2 -translate-x-1/2 z-[100] flex flex-col items-stretch gap-2 w-[calc(100%-1rem)] max-w-sm pointer-events-none"
        role="region"
        aria-label="Notifications"
      >
        {items.map((t) => {
          const tones = {
            info: "bg-surface-overlay border-edge text-ink",
            success: "bg-success-subtle/80 border-success/40 text-ink",
            danger: "bg-danger-subtle/80 border-danger/40 text-ink"
          };
          const icons = {
            info: "info",
            success: "check_circle",
            danger: "error"
          };
          return (
            <div
              key={t.id}
              className={`pointer-events-auto rounded-xl border shadow-lg px-4 py-3 backdrop-blur-md animate-slide-up flex items-start gap-3 ${tones[t.tone] || tones.info}`}
              role="status"
            >
              <span className="material-symbols-outlined text-[20px] mt-0.5">{icons[t.tone]}</span>
              <div className="flex-1 min-w-0">
                {t.title && <p className="text-sm font-semibold leading-tight">{t.title}</p>}
                {t.message && <p className="text-xs text-ink-muted mt-0.5 break-words">{t.message}</p>}
              </div>
              <button
                onClick={() => dismiss(t.id)}
                className="text-ink-muted hover:text-ink"
                aria-label="Dismiss notification"
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      push: () => null,
      dismiss: () => {},
      success: () => null,
      error: () => null,
      info: () => null
    };
  }
  return ctx;
}
